import { useEffect, useState } from "react";
import React from "react";
import api, { formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../../components/ui/dialog";
import { Textarea } from "../../components/ui/textarea";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";
import { Plus, MessageSquare, Trash2, LayoutList, Table as TableIcon } from "lucide-react";

const todayIso = () => new Date().toISOString().slice(0, 10);

export default function StatusModule() {
  const { user } = useAuth();
  const isMgmt = ["partner", "admin", "manager"].includes(user?.role);
  const canSeeAll = ["partner", "admin", "manager"].includes(user?.role);

  const [scope, setScope] = useState("mine");
  const [userFilter, setUserFilter] = useState("");
  const [clientFilter, setClientFilter] = useState("");
  const [projectFilter, setProjectFilter] = useState("");
  const [updates, setUpdates] = useState([]);
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ project_id: "", status_text: "", on_date: todayIso() });
  const [commentDraft, setCommentDraft] = useState({});
  const [viewMode, setViewMode] = useState("cards"); // 'cards' | 'table'
  const [tableExpanded, setTableExpanded] = useState({});

  const buildQuery = () => {
    const q = new URLSearchParams({ scope });
    if (userFilter) q.set("user_id", userFilter);
    if (clientFilter) q.set("client_id", clientFilter);
    if (projectFilter) q.set("project_id", projectFilter);
    return q.toString();
  };

  const load = () =>
    Promise.all([
      api.get(`/status-updates?${buildQuery()}`).then((r) => setUpdates(r.data)),
      api.get("/projects").then((r) => setProjects(r.data)),
      isMgmt
        ? Promise.all([
            api.get("/clients").then((r) => setClients(r.data)),
            api.get("/users").then((r) => setUsers(r.data)),
          ])
        : Promise.resolve(),
    ]);

  useEffect(() => {
    load();
  }, [scope, userFilter, clientFilter, projectFilter]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.project_id || !form.status_text.trim()) {
      toast.error("Project and status text are required");
      return;
    }
    setBusy(true);
    try {
      await api.post("/status-updates", form);
      toast.success("Status posted");
      setForm({ project_id: "", status_text: "", on_date: todayIso() });
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const addComment = async (id) => {
    const text = (commentDraft[id] || "").trim();
    if (!text) {
      toast.error("Comment cannot be empty");
      return;
    }
    try {
      await api.post(`/status-updates/${id}/comment`, { comment: text });
      toast.success("Comment added");
      setCommentDraft((d) => ({ ...d, [id]: "" }));
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this status update?")) return;
    try {
      await api.delete(`/status-updates/${id}`);
      toast.success("Deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="space-y-6 fade-in" data-testid="status-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="eyebrow">Status</div>
          <h1 className="font-sans text-4xl mt-2">Daily work status</h1>
          <p className="text-sm text-slate-600 mt-1">
            Share what you're working on. Partners & managers can comment back.
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <Select value={scope} onValueChange={setScope}>
            <SelectTrigger className="rounded-sm w-44" data-testid="status-scope"><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="mine">My updates</SelectItem>
              {canSeeAll && <SelectItem value="all">All team updates</SelectItem>}
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={()=>setViewMode((v)=>v === "cards" ? "table" : "cards")} className="rounded-sm" data-testid="status-view-toggle">
            {viewMode === "cards" ? <><TableIcon className="h-4 w-4 mr-2"/>Table view</> : <><LayoutList className="h-4 w-4 mr-2"/>Card view</>}
          </Button>
          {canSeeAll && scope !== "mine" && (
            <>
              <Select value={userFilter || "_all"} onValueChange={(v)=>setUserFilter(v === "_all" ? "" : v)}>
                <SelectTrigger className="rounded-sm w-44" data-testid="status-user-filter"><SelectValue placeholder="By user"/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_all">All users</SelectItem>
                  {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={clientFilter || "_all"} onValueChange={(v)=>setClientFilter(v === "_all" ? "" : v)}>
                <SelectTrigger className="rounded-sm w-44" data-testid="status-client-filter"><SelectValue placeholder="By client"/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_all">All clients</SelectItem>
                  {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.client_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </>
          )}
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-slate-900 hover:bg-slate-800 rounded-sm" data-testid="add-status-btn">
                <Plus className="h-4 w-4 mr-2"/>Post Status
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-xl">
              <DialogHeader><DialogTitle className="font-sans text-2xl">Post status update</DialogTitle></DialogHeader>
              <form onSubmit={submit} className="space-y-4">
                <div>
                  <Label className="eyebrow">Project</Label>
                  <Select value={form.project_id} onValueChange={(v)=>setForm({...form, project_id: v})}>
                    <SelectTrigger className="mt-2 rounded-sm" data-testid="status-project-select"><SelectValue placeholder="Select project"/></SelectTrigger>
                    <SelectContent>
                      {projects.map((p) => (
                        <SelectItem key={p.id} value={p.id}>
                          {p.client_name} · {p.project_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="eyebrow">Date</Label>
                  <Input type="date" value={form.on_date} onChange={(e)=>setForm({...form, on_date: e.target.value})} className="mt-2 rounded-sm" data-testid="status-date"/>
                </div>
                <div>
                  <Label className="eyebrow">Status / Update</Label>
                  <Textarea rows={5} required value={form.status_text} onChange={(e)=>setForm({...form, status_text: e.target.value})} className="mt-2 rounded-sm" placeholder="What did you work on? Any blockers? Next steps?" data-testid="status-text"/>
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={()=>setOpen(false)} className="rounded-sm">Cancel</Button>
                  <Button type="submit" disabled={busy} className="bg-slate-900 hover:bg-slate-800 rounded-sm" data-testid="status-save-btn">{busy ? "Posting..." : "Post"}</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {viewMode === "cards" ? (
      <div className="space-y-4" data-testid="status-list">
        {updates.length === 0 && (
          <div className="bg-white border border-slate-200 rounded-sm p-12 text-center text-slate-500">
            No status updates yet.
          </div>
        )}
        {updates.map((u) => (
          <div key={u.id} className="bg-white border border-slate-200 rounded-sm" data-testid={`status-card-${u.id}`}>
            <div className="p-5 border-b border-slate-100">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 text-xs">
                    <span className="font-mono text-slate-400">{u.on_date || (u.created_at || "").slice(0,10)}</span>
                    <span className="text-slate-400">·</span>
                    <span className="text-slate-700 font-medium">{u.user_name}</span>
                  </div>
                  <div className="mt-1 text-sm text-slate-600">
                    {u.client_name || "—"} · <span className="text-slate-900">{u.project_name || "—"}</span>
                  </div>
                </div>
                {(u.user_id === user.id || ["partner","admin"].includes(user.role)) && (
                  <button onClick={()=>remove(u.id)} className="text-slate-400 hover:text-red-600" title="Delete" data-testid={`status-delete-${u.id}`}>
                    <Trash2 className="h-4 w-4"/>
                  </button>
                )}
              </div>
              <p className="mt-3 text-slate-800 whitespace-pre-wrap leading-relaxed" data-testid={`status-text-${u.id}`}>
                {u.status_text || u.notes}
              </p>
            </div>
            <div className="px-5 py-3 bg-slate-50 space-y-3" data-testid={`status-thread-${u.id}`}>
              {(u.comments || []).length > 0 ? (
                (u.comments || []).map((c) => (
                  <div key={c.id} className="flex gap-3 text-sm">
                    <MessageSquare className="h-4 w-4 text-slate-400 shrink-0 mt-0.5"/>
                    <div className="flex-1">
                      <div className="text-xs text-slate-500">
                        <span className="font-medium text-slate-700">{c.by_name}</span> ({c.by_role}) · <span className="font-mono">{(c.created_at || "").slice(0,16).replace("T", " ")}</span>
                      </div>
                      <div className="text-slate-800 mt-0.5">{c.comment}</div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-xs text-slate-400 italic">No comments yet.</div>
              )}
              {isMgmt && (
                <div className="flex gap-2 pt-2 border-t border-slate-200">
                  <Input
                    value={commentDraft[u.id] || ""}
                    onChange={(e)=>setCommentDraft((d) => ({ ...d, [u.id]: e.target.value }))}
                    placeholder="Add a comment..."
                    className="rounded-sm"
                    data-testid={`status-comment-input-${u.id}`}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addComment(u.id); } }}
                  />
                  <Button onClick={()=>addComment(u.id)} size="sm" className="bg-slate-900 hover:bg-slate-800 rounded-sm" data-testid={`status-comment-btn-${u.id}`}>
                    Comment
                  </Button>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
      ) : (
      <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto" data-testid="status-table-view">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 uppercase tracking-[0.18em] text-xs">
            <tr>
              <th className="text-left p-4">Date</th>
              <th className="text-left p-4">Client</th>
              <th className="text-left p-4">Project</th>
              <th className="text-left p-4">User</th>
              <th className="text-left p-4">Status</th>
              <th className="text-left p-4">Comments</th>
              <th className="p-4"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {updates.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-slate-500">No status updates yet.</td></tr>}
            {updates.map((u) => {
              const exp = !!tableExpanded[u.id];
              return (
                <React.Fragment key={u.id}>
                  <tr className="hover:bg-slate-50 align-top" data-testid={`status-row-${u.id}`}>
                    <td className="p-4 font-mono text-xs whitespace-nowrap">{u.on_date || (u.created_at || "").slice(0,10)}</td>
                    <td className="p-4 text-slate-700">{u.client_name || "—"}</td>
                    <td className="p-4 font-medium">{u.project_name || "—"}</td>
                    <td className="p-4 text-slate-700">{u.user_name}</td>
                    <td className="p-4 text-slate-800 max-w-md">
                      <div className={exp ? "" : "line-clamp-3"}>{u.status_text || u.notes}</div>
                    </td>
                    <td className="p-4 text-slate-700">
                      <button onClick={()=>setTableExpanded((d) => ({ ...d, [u.id]: !exp }))} className="text-xs text-slate-500 hover:text-slate-900" data-testid={`status-toggle-${u.id}`}>
                        {(u.comments || []).length} comment{(u.comments||[]).length !== 1 ? "s" : ""} {exp ? "▴" : "▾"}
                      </button>
                    </td>
                    <td className="p-4 text-right">
                      {(u.user_id === user.id || ["partner","admin"].includes(user.role)) && (
                        <button onClick={()=>remove(u.id)} className="text-slate-400 hover:text-red-600" title="Delete"><Trash2 className="h-4 w-4"/></button>
                      )}
                    </td>
                  </tr>
                  {exp && (
                    <tr className="bg-slate-50">
                      <td colSpan={7} className="px-6 py-3 space-y-2" data-testid={`status-thread-row-${u.id}`}>
                        {(u.comments || []).length > 0 ? (u.comments || []).map((c) => (
                          <div key={c.id} className="flex gap-3 text-sm">
                            <MessageSquare className="h-4 w-4 text-slate-400 shrink-0 mt-0.5"/>
                            <div className="flex-1">
                              <div className="text-xs text-slate-500"><span className="font-medium text-slate-700">{c.by_name}</span> ({c.by_role}) · <span className="font-mono">{(c.created_at || "").slice(0,16).replace("T", " ")}</span></div>
                              <div className="text-slate-800 mt-0.5">{c.comment}</div>
                            </div>
                          </div>
                        )) : <div className="text-xs text-slate-400 italic">No comments yet.</div>}
                        {isMgmt && (
                          <div className="flex gap-2 pt-2">
                            <Input value={commentDraft[u.id] || ""} onChange={(e)=>setCommentDraft((d) => ({ ...d, [u.id]: e.target.value }))} placeholder="Add a comment..." className="rounded-sm" onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addComment(u.id); } }}/>
                            <Button onClick={()=>addComment(u.id)} size="sm" className="bg-slate-900 hover:bg-slate-800 rounded-sm">Comment</Button>
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      )}
    </div>
  );
}
